import { useEffect, useState } from 'react';

interface DeviceIdState {
  deviceId: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook para obtener el Device ID de MercadoPago
 * Carga el script de seguridad y obtiene el device ID automáticamente
 */
export const useDeviceId = (): DeviceIdState => {
  const [state, setState] = useState<DeviceIdState>({
    deviceId: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const loadSecurityScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Verificar si el script ya existe
        const existingScript = document.getElementById('mercado-pago-security');
        if (existingScript) {
          resolve();
          return;
        }

        // Crear y cargar el script de seguridad
        const script = document.createElement('script');
        script.id = 'mercado-pago-security';
        script.src = 'https://www.mercadopago.com/v2/security.js';
        script.setAttribute('view', 'checkout');
        script.async = true;

        script.onload = () => {
          console.log('Script de seguridad de MercadoPago cargado correctamente');
          resolve();
        };

        script.onerror = () => {
          reject(new Error('Error al cargar el script de seguridad de MercadoPago'));
        };

        document.head.appendChild(script);
      });
    };

    const getDeviceId = (): string | null => {
      // Verificar si la variable global está disponible
      if (typeof window !== 'undefined' && (window as any).MP_DEVICE_SESSION_ID) {
        return (window as any).MP_DEVICE_SESSION_ID;
      }
      return null;
    };

    const initializeDeviceId = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        // Cargar el script de seguridad
        await loadSecurityScript();

        // Esperar un momento para que se genere el device ID
        // El script puede tardar un poco en generar el ID
        const checkDeviceId = () => {
          const deviceId = getDeviceId();
          if (deviceId) {
            setState({
              deviceId,
              isLoading: false,
              error: null,
            });
          } else {
            // Reintentar después de un breve delay
            setTimeout(checkDeviceId, 100);
          }
        };

        // Iniciar la verificación del device ID
        checkDeviceId();

        // Timeout de seguridad para evitar esperar indefinidamente
        setTimeout(() => {
          setState(prev => {
            if (prev.isLoading) {
              return {
                deviceId: null,
                isLoading: false,
                error: 'Timeout: No se pudo obtener el Device ID en 10 segundos',
              };
            }
            return prev;
          });
        }, 10000); // 10 segundos de timeout

      } catch (error) {
        console.error('Error obteniendo Device ID:', error);
        setState({
          deviceId: null,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Error desconocido',
        });
      }
    };

    initializeDeviceId();
  }, []);

  return state;
};

// Extender la interfaz Window para incluir MP_DEVICE_SESSION_ID
declare global {
  interface Window {
    MP_DEVICE_SESSION_ID?: string;
  }
}
